/** @jsx jsx */
import React, { useCallback, useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import { Checkbox } from '@atlaskit/checkbox';
import { xcss, Flex, Box } from '@atlaskit/primitives';
import {
	Card,
	TitleBlock,
	SmartLinkSize,
	SmartLinkTheme,
	SmartLinkPosition,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { IssueLinkType } from '@atlassian/jira-issue-shared-types/src/common/types/linked-issue-type.tsx';
import { ENTITY_FIELD_KEYS } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useRelatedIssuesActions,
	useRelatedIssuesState,
	type RelatedIssue,
	type InvokedFrom,
} from '../../controllers/context.tsx';
import type { onIssueLinkSave } from '../types.tsx';
import { LinkSplitButton } from '../link-split-button/index.tsx';
import { getRelatesToIssueLinkType, useIssueLinkTypesWithRelatesTo } from '../../utils/index.tsx';
import messages from './messages.tsx';

const ACTION_BUTTON_COMPONENT_SELECTOR =
	'ai-improve-issue-ai-related-issues-draft-list-row-action-button';

type Props = {
	onIssueLinkSave: onIssueLinkSave;
	invokedFrom?: InvokedFrom;
	onCopyButtonClick?: (issueKey: string) => void;
	onSimilarIssueLinkGICButtonClick?: ((relatedIssue: RelatedIssue) => void) | null;
	issueLinkTypes: IssueLinkType[];
};

export const RelatedIssuesDraftListIssueView = ({ onIssueLinkSave, issueLinkTypes }: Props) => {
	const { formatMessage } = useIntl();
	const { resourceSuggestions } = useRelatedIssuesState();
	const { acceptSuggestion, setResourceSuggestionStatus } = useRelatedIssuesActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	let relatesToIssueLinkType: IssueLinkType;
	if (fg('remove-issue-link-types-dfo')) {
		relatesToIssueLinkType = getRelatesToIssueLinkType(issueLinkTypes);
	} else {
		// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
		const issueLinkTypesWithRelatesToData = useIssueLinkTypesWithRelatesTo();
		relatesToIssueLinkType = issueLinkTypesWithRelatesToData.relatesToIssueLinkType;
	}

	const shownSuggestions = resourceSuggestions?.filter(
		(suggestion) =>
			suggestion.resourceStatus === 'DRAFT' || suggestion.resourceStatus === 'SELECTED',
	);

	const isAllSelected = useMemo(() => {
		return resourceSuggestions?.every(
			(suggestion) =>
				suggestion.resourceStatus === 'SELECTED' || suggestion.resourceStatus === 'ACCEPTED',
		);
	}, [resourceSuggestions]);

	const onSelectAllChange = useCallback(() => {
		shownSuggestions?.forEach((suggestion) => {
			setResourceSuggestionStatus(suggestion.resource, isAllSelected ? 'DRAFT' : 'SELECTED');
		});
	}, [shownSuggestions, setResourceSuggestionStatus, isAllSelected]);

	const onSingleSuggestionAccept = useCallback(
		(linkSuggestionsRequest: RelatedIssue, issueLinkType: IssueLinkType) => {
			const optimisticId = `OPTIMISTIC_LINKED_ISSUE_${Date.now()}_${linkSuggestionsRequest.id}`;

			acceptSuggestion(linkSuggestionsRequest);

			onIssueLinkSave(
				optimisticId,
				linkSuggestionsRequest,
				issueLinkType,
				createAnalyticsEvent({}),
			);

			getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(ENTITY_FIELD_KEYS.ISSUE_LINKS, {
				analytics: createAnalyticsEvent({}),
				attributes: {
					fieldType: ENTITY_FIELD_KEYS.ISSUE_LINKS,
					actionTaken: 'add',
				},
			});

			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult actioned',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiResultAction: 'inlineIssueAccepted',
					isRelatedToIssueLinkType: issueLinkType.id === relatesToIssueLinkType?.id,
					...(fg('thor-additional-analytics-suggested-related-issue') && {
						linkedIssueId: linkSuggestionsRequest.id,
					}),
				},
			);
		},
		[acceptSuggestion, createAnalyticsEvent, onIssueLinkSave, relatesToIssueLinkType?.id],
	);

	const cards = shownSuggestions?.map(({ resource, resourceStatus }) => {
		const ItemNode = (
			<Box xcss={baseRowStyles} key={resource.id}>
				<Flex alignItems="center">
					<Card
						appearance="block"
						url={resource.issueLink}
						showHoverPreview
						ui={{
							size: SmartLinkSize.Medium,
							hideElevation: true,
							hideBackground: true,
							theme: SmartLinkTheme.Black,
							hidePadding: true,
						}}
					>
						<TitleBlock
							hideTitleTooltip
							position={SmartLinkPosition.Center}
							testId={`ai-related-issues.ui.related-issues-draft-list.title-block-${resource.id}`}
						/>
					</Card>
				</Flex>
				<div data-component-selector={`${ACTION_BUTTON_COMPONENT_SELECTOR}`}>
					<Flex xcss={actionStyles}>
						<LinkSplitButton
							onButtonClick={(issueLinkType) => onSingleSuggestionAccept(resource, issueLinkType)}
						/>
					</Flex>
				</div>
			</Box>
		);

		return (
			<div
				key={resource.id}
				css={[fg('jira-ai-suggested-works-ui-update') ? hoverFocusStyles : hoverFocusStylesOld]}
				{...(fg('jfp_a11y_team_list_for_link_related_work_items') && {
					role: 'listitem',
				})}
			>
				<Checkbox
					key={resource.id}
					label={ItemNode}
					onChange={(e) =>
						setResourceSuggestionStatus(resource, e.target.checked ? 'SELECTED' : 'DRAFT')
					}
					isChecked={resourceStatus === 'SELECTED'}
					{...(fg('jfp_a11y_team_appropriate_accessible_name_fix')
						? {
								'aria-label': formatMessage(messages.checkboxLabel, {
									issueLabel: `${resource.issueKey}: ${resource.issueSummary}`,
								}),
							}
						: {
								'aria-label': formatMessage(messages.link),
								'aria-labelledby': `ai-related-issues.ui.related-issues-draft-list.title-block-${resource.id}`,
							})}
					xcss={styles.checkbox}
				/>
			</div>
		);
	});

	return (
		<Box
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(fg('jfp_a11y_team_list_for_link_related_work_items') && {
				xcss: listContainer,
				role: 'list',
			})}
		>
			<Box
				xcss={cardStyles}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(fg('jfp_a11y_team_list_for_link_related_work_items') && {
					role: 'listitem',
				})}
			>
				<Checkbox
					label={formatMessage(isAllSelected ? messages.deselectAll : messages.selectAll)}
					onChange={onSelectAllChange}
					isChecked={isAllSelected}
					xcss={styles.checkbox}
				/>
			</Box>
			{cards}
		</Box>
	);
};

const hoverFocusStylesOld = css({
	'&:hover, &:focus-within': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"[data-component-selector='ai-improve-issue-ai-related-issues-draft-list-row-action-button']": {
			opacity: '1',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"[data-component-selector='ai-improve-issue-ai-related-issues-draft-list-row-action-button']": {
		opacity: '0',
	},
	boxSizing: 'border-box',
	borderTop: `1px solid ${token('color.border')}`,
	paddingBlock: token('space.100'),
	paddingLeft: token('space.150'),
	paddingRight: token('space.250'),
});

const listContainer = xcss({
	padding: 'space.0',
	margin: 'space.0',
});

const hoverFocusStyles = css({
	'&:hover, &:focus-within': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"[data-component-selector='ai-improve-issue-ai-related-issues-draft-list-row-action-button']": {
			opacity: '1',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"[data-component-selector='ai-improve-issue-ai-related-issues-draft-list-row-action-button']": {
		opacity: '0',
	},
	boxSizing: 'border-box',
	borderTop: `1px solid ${token('color.border')}`,
	paddingBlock: token('space.100'),
	paddingLeft: token('space.150'),
	paddingRight: token('space.200'),
});

const baseRowStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
});

const actionStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	columnGap: 'space.150',
});

const cardStyles = xcss({
	paddingInline: 'space.150',
	boxSizing: 'border-box',
	borderTop: `1px solid ${token('color.border')}`,
	paddingBlock: 'space.100',
});

const styles = cssMap({
	checkbox: {
		alignItems: 'center',
	},
});
